// P4-SCHED Release 2-A — the procurement chain (SCHEDULE_INTELLIGENCE_MODULE.md
// §39 Procurement Schedule Generation, §40 Long-Lead Risk; P4_SCHEDULE_DURATION.md
// "Procurement linkage"). Generated from the canonical assumed-equipment model
// (ProcessEquipment, db/24) — one chain per record that must be procured:
//
//   Submittal → Approval → Release (milestone) → Fabrication / lead time →
//   Shipping → Required on site (milestone) → Set / rig
//
// Durations, as everywhere in the schedule: submittal / approval / shipping /
// set come from versioned ProductionRateStandards (fixed-day types
// EQUIP_SUBMITTAL / EQUIP_APPROVAL / EQUIP_SHIPPING / EQUIP_SET — nothing
// seeded, "Duration required" until entered); fabrication is the record's own
// LEAD TIME in weeks (ProcessEquipment.LeadTimeWeeks + LeadTimeSource — quoted
// vs estimator assumption is preserved, never blended) converted to working
// days by the work pattern. No lead time → "Lead time required", never a guess.
//
// Long-lead risk (§40) is DERIVED from the network: the set activity's early
// start with the procurement chain versus without it. A positive difference
// means procurement, not construction, decides when the equipment can be set.
//
// Activity keys: 'proc:<EquipmentID>:<STEP>' — re-keyed through the equipment
// identity map when a revision is copied (db/90). Pure functions, no DB.

import { calculateActivityDuration, calendarFromHeader, cpm } from "./scheduleEngine";

export const PROCUREMENT_STEPS = [
  { step: "SUBMITTAL",   name: "Submittal",                phase: "design",        kind: "fixed",     standardType: "EQUIP_SUBMITTAL" },
  { step: "APPROVAL",    name: "Submittal approval",       phase: "design",        kind: "fixed",     standardType: "EQUIP_APPROVAL" },
  { step: "RELEASE",     name: "Release for fabrication",  phase: "procurement",   kind: "milestone" },
  { step: "FABRICATION", name: "Fabrication / lead time",  phase: "manufacturing", kind: "lead_time" },
  { step: "SHIPPING",    name: "Shipping",                 phase: "procurement",   kind: "fixed",     standardType: "EQUIP_SHIPPING" },
  { step: "ROS",         name: "Required on site",         phase: "construction",  kind: "milestone" },
  { step: "SET",         name: "Set / rig",                phase: "construction",  kind: "fixed",     standardType: "EQUIP_SET" },
];
export const LEAD_TIME_QUANTITY_TYPE = "LEAD_TIME_WEEKS";

// Where a lead time came from — kept separate so a quoted figure is never
// confused with an estimator's assumption (P4_SCHEDULE_DURATION.md).
export const LEAD_TIME_SOURCES = [
  { key: "estimator",    label: "Estimator assumption" },
  { key: "quote",        label: "Vendor quote" },
  { key: "manufacturer", label: "Manufacturer published" },
  { key: "library",      label: "Equipment library default" },
];
export const leadTimeSourceLabel = (k) => LEAD_TIME_SOURCES.find(s => s.key === k)?.label || (k ? String(k) : "not stated");

export const procurementKey = (equipmentId, step) => `proc:${equipmentId}:${step}`;
export function parseProcurementKey(key) {
  const m = /^proc:(\d+):([A-Z_]+)$/.exec(String(key || ""));
  return m ? { equipmentId: +m[1], step: m[2] } : null;
}

// Which records get a chain: an installed quantity, and a lifecycle that means
// it is being bought for this project ('existing' equipment is already there;
// 'future' is outside this contract). Owner-furnished records still get the
// chain — their delivery still gates the set — and the detail says who furnishes.
export function procurementScope(equipment = []) {
  return (equipment || []).filter(e => (+e.Qty || 0) > 0 && !["existing", "future"].includes(String(e.Status || "").toLowerCase()));
}

const num = (v) => (v == null || v === "" || !Number.isFinite(+v) ? null : +v);
const label = (e) => e.Tag ? `${e.Tag}${e.Description ? ` · ${e.Description}` : ""}` : (e.Description || `Equipment #${e.EquipmentID}`);

// Discipline for the schedule's discipline filter, from the record's free-text category.
export function disciplineForEquipment(e) {
  const c = `${e.Category || ""} ${e.Description || ""}`.toLowerCase();
  if (/\b(ahu|hvac|chiller|boiler|fan|pump|exhaust|condens|cooling|heating|air handler|rtu|fcu|vav|humidif|dehumid)\b/.test(c)) return "mech";
  if (/\b(panel|switchgear|transformer|mcc|ups|generator|distribution|breaker|lighting)\b/.test(c)) return "elec";
  if (/\b(controls?|bms|plc|scada|instrument)\b/.test(c)) return "controls";
  return "process";
}

// Build the chain for every record in scope.
//   equipment       ProcessEquipment rows (sp_GetEquipmentRegister Table1)
//   standardsByType current ProductionRateStandards by ActivityType
//   header          ExecutionSchedules row (work pattern)
//   existingByKey   stored ScheduleActivities by ActivityKey (estimator inputs preserved)
//   archKeys        Set of the architectural activity keys present (for the cross links)
// Returns { activities, dependencies, warnings, unscheduled, chains:[{equipmentId, label, keys}] }
export function generateProcurementActivities({ equipment = [], standardsByType = {}, header = {}, existingByKey = {}, archKeys = new Set() } = {}) {
  const cal = calendarFromHeader(header || {});
  const activities = [], dependencies = [], warnings = [], unscheduled = [], chains = [];
  const scope = procurementScope(equipment);
  let order = 5000;
  for (const e of scope) {
    const lt = num(e.LeadTimeWeeks);
    const keys = {};
    for (const s of PROCUREMENT_STEPS) {
      const key = procurementKey(e.EquipmentID, s.step);
      keys[s.step] = key;
      const std = s.standardType ? (standardsByType[s.standardType] || null) : null;
      const prior = existingByKey[key] || null;
      const isMilestone = s.kind === "milestone";
      const minDays = prior?.MinimumDurationDays ?? std?.MinimumDurationDays ?? (isMilestone ? 0 : 1);
      const a = {
        activityKey: key, origin: "derived", activityType: s.standardType || `EQUIP_${s.step}`, name: `${s.name} — ${label(e)}`,
        phase: s.phase, discipline: disciplineForEquipment(e), kind: s.kind, isMilestone,
        equipmentId: e.EquipmentID, step: s.step,
        sourceQuantityType: s.kind === "lead_time" ? LEAD_TIME_QUANTITY_TYPE : null,
        sourceQuantity: s.kind === "lead_time" ? lt : null,
        sourceUnit: s.kind === "lead_time" ? "WK" : null,
        sourceDetail: [{ equipmentId: e.EquipmentID, tag: e.Tag || null, description: e.Description || null, qty: +e.Qty || 0, furnishedBy: e.FurnishedBy || null, installedBy: e.InstalledBy || null,
          ...(s.kind === "lead_time" ? { leadTimeWeeks: lt, leadTimeSource: e.LeadTimeSource || null, leadTimeQuoteId: e.LeadTimeQuoteID ?? null } : {}) }],
        standardId: std?.StandardID ?? null, standardVersion: std?.Version ?? null,
        ratePerCrewHour: null,
        fixedDurationDays: s.kind === "fixed" ? (std?.FixedDurationDays ?? null) : null,
        crewCount: prior?.CrewCount ?? 1, crewSize: prior?.CrewSize ?? std?.DefaultCrewSize ?? null,
        shiftsPerDay: prior?.ShiftsPerDay ?? null, hoursPerShift: prior?.HoursPerShift ?? null, overtimeHoursPerShift: prior?.OvertimeHoursPerShift ?? null,
        minimumDurationDays: minDays, displayOrder: (order += 10),
      };
      const calc = calculateActivityDuration({
        kind: s.kind, quantity: a.sourceQuantity, leadTimeWeeks: lt, fixedDurationDays: a.fixedDurationDays,
        crewCount: a.crewCount, minimumDurationDays: minDays, calendar: cal,
        activity: { ShiftsPerDay: a.shiftsPerDay, HoursPerShift: a.hoursPerShift, OvertimeHoursPerShift: a.overtimeHoursPerShift },
        // procurement waits are calendar realities — the congestion / occupied / complexity factors do not apply
        factors: { congestion: 1, occupied: 1, complexity: 1 },
      });
      a.calculatedDurationDays = calc.calculatedDurationDays;
      a.calc = calc;
      activities.push(a);
    }
    if (lt == null) unscheduled.push({ code: "lead_time_required", equipmentId: e.EquipmentID, message: `${label(e)} has no lead time — enter it on the equipment record (Assumed Equipment) so fabrication can be scheduled.` });
    // The chain, strictly finish → start.
    const seq = PROCUREMENT_STEPS.map(s => keys[s.step]);
    for (let i = 1; i < seq.length; i++) dependencies.push({ predecessorKey: seq[i - 1], successorKey: seq[i], relationshipType: "FS", lagDays: 0 });
    // Cross links to the architectural network where those activities exist.
    if (archKeys.has("arch:NTP")) dependencies.push({ predecessorKey: "arch:NTP", successorKey: keys.SUBMITTAL, relationshipType: "FS", lagDays: 0 });
    if (archKeys.has("arch:MOBILIZATION")) dependencies.push({ predecessorKey: "arch:MOBILIZATION", successorKey: keys.SET, relationshipType: "FS", lagDays: 0 });
    if (archKeys.has("arch:PUNCH")) dependencies.push({ predecessorKey: keys.SET, successorKey: "arch:PUNCH", relationshipType: "FS", lagDays: 0 });
    chains.push({ equipmentId: e.EquipmentID, label: label(e), keys, leadTimeWeeks: lt, leadTimeSource: e.LeadTimeSource || null, furnishedBy: e.FurnishedBy || null });
  }
  const required = activities.filter(a => a.calculatedDurationDays == null && !a.isMilestone && a.kind !== "lead_time");
  if (required.length) warnings.push({ code: "procurement_durations_required", count: required.length, message: `${required.length} procurement activit${required.length === 1 ? "y has" : "ies have"} no fixed-duration standard (EQUIP_SUBMITTAL / EQUIP_APPROVAL / EQUIP_SHIPPING / EQUIP_SET) — enter them under Setup ▸ Estimate Engine ▸ Production Standards.` });
  if (unscheduled.length) warnings.push({ code: "lead_times_required", count: unscheduled.length, message: `${unscheduled.length} equipment record${unscheduled.length === 1 ? "" : "s"} ha${unscheduled.length === 1 ? "s" : "ve"} no lead time.` });
  return { activities, dependencies, warnings, unscheduled, chains };
}

// §40 — per equipment record, from an EVALUATED schedule (evaluateSchedule's
// rows: ActivityKey, eff.effective, cpm) and its stored dependencies: does the
// procurement chain, rather than construction, decide when the set happens?
//   impactDays = ES(set, full network) − ES(set, without the chain's own links)
// Returns [{ equipmentId, label, leadTimeWeeks, leadTimeSource, rosDay, setDay,
//            impactDays, critical, status: 'risk' | 'ok' | 'incomplete' }]
export function assessLongLeadRisk(evaluated, dependencies = [], { criticalFloatThreshold = 0 } = {}) {
  const rows = evaluated?.activities || [];
  const byKey = new Map(rows.map(r => [r.ActivityKey, r]));
  const idOf = (r) => r.ActivityID ?? r.ActivityKey;
  const keyOfId = new Map(rows.map(r => [idOf(r), r.ActivityKey]));
  const acts = rows.map(r => ({ id: idOf(r), duration: r.eff?.effective ?? 0 }));
  const deps = dependencies.map(d => ({ predId: d.PredecessorActivityID ?? d.predecessorId, succId: d.SuccessorActivityID ?? d.successorId, type: d.RelationshipType || "FS", lag: +d.LagDays || 0 }));
  const full = cpm(acts, deps, { criticalFloatThreshold });
  // without any link INTO a set activity from its own chain (ROS → SET)
  const withoutChain = cpm(acts, deps.filter(d => {
    const s = parseProcurementKey(keyOfId.get(d.succId)), p = parseProcurementKey(keyOfId.get(d.predId));
    return !(s && p && s.step === "SET" && p.equipmentId === s.equipmentId);
  }), { criticalFloatThreshold });
  const out = [];
  for (const r of rows) {
    const pk = parseProcurementKey(r.ActivityKey);
    if (!pk || pk.step !== "SET") continue;
    const ros = byKey.get(procurementKey(pk.equipmentId, "ROS"));
    const chain = PROCUREMENT_STEPS.map(s => byKey.get(procurementKey(pk.equipmentId, s.step))).filter(Boolean);
    const incomplete = full.cycles || chain.some(a => a.eff?.required);
    const setId = idOf(r);
    const es = full.byId[setId]?.ES ?? null, esNo = withoutChain.byId[setId]?.ES ?? null;
    const impactDays = es != null && esNo != null ? Math.max(0, es - esNo) : null;
    const fab = byKey.get(procurementKey(pk.equipmentId, "FABRICATION"));
    const detail = fab?.SourceDetailJson ? safeJson(fab.SourceDetailJson)?.[0] : null;
    out.push({
      equipmentId: pk.equipmentId,
      label: (r.Name || "").replace(/^Set \/ rig — /, ""),
      leadTimeWeeks: fab?.SourceQuantity ?? detail?.leadTimeWeeks ?? null,
      leadTimeSource: detail?.leadTimeSource ?? null,
      rosDay: ros ? full.byId[idOf(ros)]?.EF ?? null : null,
      setDay: es,
      impactDays,
      critical: !!full.byId[setId]?.critical,
      status: incomplete ? "incomplete" : (impactDays > 0 ? "risk" : "ok"),
    });
  }
  return out.sort((a, b) => (b.impactDays ?? -1) - (a.impactDays ?? -1));
}
const safeJson = (s) => { try { return typeof s === "string" ? JSON.parse(s) : s; } catch { return null; } };
